/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Grid, TextField } from '@coursera/cds-core';

import ProgrammingIseChangeNotification from 'bundles/author-assignment-grading/components/programming/ProgrammingIseChangeNotification';
import {
  programmingIseContextStateHasChanges,
  useProgrammingProctoringConfigurationContext,
} from 'bundles/author-assignment-grading/components/programming/context/ProgrammingProctoringConfigurationContext';
import {
  doesAttemptSummaryHaveSubmissionLimit,
  doesAttemptSummaryHaveTimeLimit,
} from 'bundles/programming-common/utils/timed';

import _t from 'i18n!nls/author-assignment-grading';

type ValidationStatus = 'success' | 'error' | undefined;

const styles = {
  marginTop: css`
    margin-top: 24px;
  `,
};

// Inner UI editor for easier unit-testing
export const ProgrammingLearnerItemSettingExceptionsProctoringConfigurationAttemptSettingsEditor = () => {
  const { programmingIseContextState, setProgrammingIseContextState, attemptSummary } =
    useProgrammingProctoringConfigurationContext();
  const [submissionLimitValidation, setSubmissionLimitValidation] = useState<ValidationStatus>();
  const [timeLimitValidation, setTimeLimitValidation] = useState<ValidationStatus>();

  const hasSubmissionLimit = attemptSummary && doesAttemptSummaryHaveSubmissionLimit(attemptSummary);
  const hasTimeLimit = attemptSummary && doesAttemptSummaryHaveTimeLimit(attemptSummary);

  const { allowedDurationSeconds, allowedSubmissions } = programmingIseContextState;
  // allowedDurationSeconds is never undefined, but we need to check for it to satisfy the type checker
  const allowedDurationMinutes = allowedDurationSeconds ? allowedDurationSeconds / 60 : 0;

  const isProgrammingIseContextStateValid = submissionLimitValidation !== 'error' && timeLimitValidation !== 'error';

  const onChangeMaximumNumberOfSubmissions = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedSubmissionsStr = event.target.value;
    const newAllowedSubmissions = allowedSubmissionsStr === '' ? 0 : parseInt(allowedSubmissionsStr, 10);
    if (newAllowedSubmissions <= 0) {
      setSubmissionLimitValidation('error');
    } else {
      setSubmissionLimitValidation(undefined);
      setProgrammingIseContextState({
        allowedSubmissions: newAllowedSubmissions,
      });

      // For anything with a time limit, we prompt the learner to start the attempt, so that the timer corresponds to their acknowledgement.
      // For submission limit overrides, we can just start it for them.
      // The attempt needs to be started in order to accept submissions, in case the learner is in the lab while ISE is being applied.
      // The attempt would start if they launched the lab again, but we can't guarantee that to happen.
      if (
        attemptSummary?.__typename === 'SubmissionLimitSummary' &&
        // Frontend will try to avoid starting the attempt when the workspace doesn't exist.
        // The workspace needs to exist in order for this to function, so we avoid starting in NOT_STARTED state.
        // Note that CreateAttemptOverride traditionally moves attempt status to NOT_STARTED, so you might think that two ISEs in a row would fail,
        // but if there was a chain ISE on an open attempt, this code should theoretically repeatedly chain startAttemptNow over and over,
        // to keep the attempt open.
        // Also note that the attempt summary response immediately-after will reflect the updated attempt status, because everything behind the API is blocking.
        // The frontend is relying on this blocking property from the backend.
        attemptSummary?.attemptStatus !== 'NOT_STARTED'
      ) {
        // TODO(awa/riwong): The text input (UI) callback shouldn't have this logic, it should be guarded by the data store (context).
        // In case in the future we make other UI components that write similar properties to the context, it would be confusing.
        // Today, this is the only place that sets startAttemptNow: true
        setProgrammingIseContextState({
          startAttemptNow: true,
        });
      }
    }
  };

  const onChangeTotalTimeInMinutes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allowedDurationMinutesStr = event.target.value;
    const newAllowedDurationMinutes = allowedDurationMinutesStr === '' ? 0 : parseInt(allowedDurationMinutesStr, 10);
    if (newAllowedDurationMinutes <= 0) {
      setTimeLimitValidation('error');
    } else {
      setTimeLimitValidation(undefined);
      setProgrammingIseContextState({
        allowedDurationSeconds: newAllowedDurationMinutes * 60,
      });
    }
  };

  if (!hasSubmissionLimit && !hasTimeLimit) {
    return null;
  }

  return (
    <Grid container>
      {hasSubmissionLimit && (
        <Grid item xs={12} css={styles.marginTop}>
          <TextField
            type="number"
            inputProps={{ inputMode: 'numeric', min: 0 }}
            label={_t('Maximum Number of Submissions')}
            defaultValue={allowedSubmissions}
            onChange={onChangeMaximumNumberOfSubmissions}
            validationLabel={submissionLimitValidation === 'error' ? _t('Number must be greater than 0') : undefined}
            validationStatus={submissionLimitValidation}
          />
        </Grid>
      )}
      {hasTimeLimit && (
        <Grid item xs={12} css={styles.marginTop}>
          <TextField
            type="number"
            inputProps={{ inputMode: 'numeric', min: 0 }}
            label={_t('Total Time in Minutes')}
            defaultValue={allowedDurationMinutes}
            onChange={onChangeTotalTimeInMinutes}
            validationLabel={timeLimitValidation === 'error' ? _t('Number must be greater than 0') : undefined}
            validationStatus={timeLimitValidation}
          />
        </Grid>
      )}
      {attemptSummary &&
        programmingIseContextStateHasChanges(attemptSummary, programmingIseContextState) &&
        isProgrammingIseContextStateValid && <ProgrammingIseChangeNotification />}
    </Grid>
  );
};

export default ProgrammingLearnerItemSettingExceptionsProctoringConfigurationAttemptSettingsEditor;
